import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowUUpLeft, Trash, Warning, ArrowLeft, TrashSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const SECTIONS = [
  { key: "projects", label: "Projects", listUrl: "/trash/projects",
    primary: (p) => ({ title: p.name, subtitle: `${p.type} · ${p.client_name || p.location || "—"}` }) },
  { key: "vendors", label: "Vendors", listUrl: "/trash/vendors",
    primary: (v) => ({ title: v.name, subtitle: v.category || v.email || "—" }) },
  { key: "quotations", label: "Quotations", listUrl: "/trash/quotations",
    primary: (q) => ({ title: q.title, subtitle: q.vendor_name || q.category || "—" }) },
];

function TrashSection({ section }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]);
  const [pendingPurge, setPendingPurge] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(section.listUrl).then((r) => { setItems(r.data); setSelected([]); }).catch(() => setItems([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [section.key]);

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = items && items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allSelected ? [] : items.map((p) => p.id));

  const restore = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/${section.key}/bulk-restore`, { ids });
      toast.success(`${data.restored} ${section.key === "projects" ? "project" : section.key.slice(0, -1)}${data.restored === 1 ? "" : "s"} restored`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmPurge = async () => {
    if (!pendingPurge?.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/${section.key}/bulk-purge`, { ids: pendingPurge.map((p) => p.id) });
      toast.success(`${data.purged} permanently deleted`);
      setPendingPurge(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const selectedItems = () => (items || []).filter((p) => selected.includes(p.id));

  if (items === null) return <div className="label-caps animate-pulse py-8">Loading…</div>;

  if (items.length === 0) {
    return (
      <div className="border border-dashed border-border p-16 text-center" data-testid={`trash-empty-${section.key}`}>
        <Trash size={32} weight="bold" className="mx-auto mb-4 text-muted-foreground" />
        <p className="font-heading text-xl font-bold mb-2">No deleted {section.label.toLowerCase()}</p>
        <p className="text-muted-foreground text-sm">Deleted {section.label.toLowerCase()} appear here for 30 days.</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-muted-foreground mr-auto">{selected.length ? `${selected.length} selected` : `${items.length} in trash`}</span>
        {selected.length > 0 && (
          <>
            <Button variant="outline" disabled={busy} onClick={() => restore(selected)} className="rounded-none" data-testid={`restore-selected-${section.key}`}>
              <ArrowUUpLeft size={16} className="mr-1" /> Restore ({selected.length})
            </Button>
            <Button variant="destructive" disabled={busy} onClick={() => setPendingPurge(selectedItems())} className="rounded-none" data-testid={`purge-selected-${section.key}`}>
              <Trash size={16} className="mr-1" /> Delete forever
            </Button>
          </>
        )}
        <Button variant="destructive" disabled={busy} onClick={() => setPendingPurge(items)} className="rounded-none" data-testid={`empty-trash-${section.key}`}>
          <TrashSimple size={16} className="mr-1" /> Empty
        </Button>
      </div>

      <div className="border border-border" data-testid={`trash-list-${section.key}`}>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-black" data-testid={`select-all-${section.key}`} /></TableHead>
              <TableHead>{section.label.slice(0, -1)}</TableHead>
              <TableHead className="text-right">Expires in</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => {
              const { title, subtitle } = section.primary(p);
              return (
                <TableRow key={p.id} data-testid={`trash-row-${p.id}`} className="hover:bg-secondary transition-colors duration-200">
                  <TableCell><input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} className="h-4 w-4 accent-black" data-testid={`select-trash-${p.id}`} /></TableCell>
                  <TableCell>
                    <div className="font-medium">{title}</div>
                    <div className="text-xs text-muted-foreground capitalize">{subtitle}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`text-xs font-bold px-2 py-0.5 border ${p.days_left <= 7 ? "text-destructive border-destructive" : "text-muted-foreground border-border"}`}>
                      {p.days_left} day{p.days_left === 1 ? "" : "s"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={() => restore([p.id])} disabled={busy} data-testid={`restore-${p.id}`} title="Restore" className="text-muted-foreground hover:text-emerald-700 mr-3 align-middle"><ArrowUUpLeft size={16} className="inline" /></button>
                    <button onClick={() => setPendingPurge([p])} disabled={busy} data-testid={`purge-${p.id}`} title="Delete forever" className="text-muted-foreground hover:text-destructive align-middle"><Trash size={16} className="inline" /></button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!pendingPurge} onOpenChange={(o) => !o && setPendingPurge(null)}>
        <DialogContent className="rounded-none" data-testid="purge-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Warning size={20} weight="fill" className="text-destructive" /> Permanently delete {pendingPurge?.length === 1 ? "item" : `${pendingPurge?.length} items`}?</DialogTitle></DialogHeader>
          {pendingPurge && (
            <div className="space-y-4">
              <DialogDescription className="text-sm text-muted-foreground">
                This permanently removes {pendingPurge.length === 1 ? "this record" : "these records"}{section.key === "projects" ? ", including its phases, cost items and receipts" : ""}. <strong>This cannot be undone.</strong>
              </DialogDescription>
              <div className="border border-border max-h-48 overflow-y-auto">
                {pendingPurge.map((p) => (
                  <div key={p.id} className="px-3 py-2 border-b border-border last:border-b-0 text-sm font-medium truncate">{section.primary(p).title}</div>
                ))}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setPendingPurge(null)} className="rounded-none">Cancel</Button>
                <Button variant="destructive" onClick={confirmPurge} disabled={busy} className="rounded-none" data-testid="purge-confirm-button">
                  <Trash size={16} className="mr-1" />{busy ? "Deleting…" : "Delete forever"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function TrashProjects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["owner", "admin"].includes(user?.role);
  const [searchParams] = useSearchParams();
  const initial = SECTIONS.some((s) => s.key === searchParams.get("tab")) ? searchParams.get("tab") : "projects";
  const [tab, setTab] = useState(initial);

  if (!isManager) {
    return (
      <div>
        <PageHeader label="Recently deleted" title="Trash" />
        <div className="p-4 sm:p-8">
          <div className="border border-dashed border-border p-16 text-center">
            <p className="text-muted-foreground text-sm">Only the team owner or an admin can manage the trash.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader label="Recently deleted" title="Trash">
        <Button variant="outline" onClick={() => navigate("/projects")} className="rounded-none" data-testid="back-to-projects-button">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
      </PageHeader>
      <div className="p-4 sm:p-8">
        <p className="text-sm text-muted-foreground mb-6">Deleted items are kept for <strong>30 days</strong>, then permanently removed. Restore anything you still need.</p>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none mb-6" data-testid="trash-tabs">
            {SECTIONS.map((s) => (
              <TabsTrigger key={s.key} value={s.key} className="rounded-none" data-testid={`trash-tab-${s.key}`}>{s.label}</TabsTrigger>
            ))}
          </TabsList>
          {SECTIONS.map((s) => (
            <TabsContent key={s.key} value={s.key}>
              <TrashSection section={s} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
